import React from 'react';
import { Activity, AlertTriangle, Clock, Copy, Eye, KeyRound, Lock, MoreHorizontal, Pencil, RefreshCw, Share2, Trash2, Users } from 'lucide-react';
import { toast } from 'sonner';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { copyToClipboard } from '../../lib/clipboard';
import { formatDate, publicBaseUrl, relativeTime } from '../../lib/developerApi';
import { KeyStatusPill, Note, ScopeChips, StatTile } from './parts';

const REASONS = {
    expires_soon: 'Expires soon',
    expired: 'Expired',
    default_owner_inactive: 'Default owner is no longer active',
};

export default function KeysTab({ keys, stats, onOpen, onEditPermissions, onRoll, onRevoke }) {
    const base = publicBaseUrl();
    const attention = stats?.needs_attention?.[0];
    const errorRate = stats ? `${((stats.error_rate_24h || 0) * 100).toFixed(1)}%` : '—';

    const copyBase = async () => {
        if (await copyToClipboard(base)) toast.success('Base URL copied');
    };

    return (
        <div className="flex flex-col gap-5" data-testid="keys-tab">
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                <StatTile label="Active keys" icon={KeyRound} value={stats?.active_keys ?? '—'} hint={stats ? `${stats.held_keys ?? stats.active_keys} of ${stats.max_keys} slots used (expired keys count until revoked)` : ''} />
                <StatTile label="Requests · 24h" icon={Activity} value={(stats?.requests_24h ?? 0).toLocaleString()} hint={`${(stats?.requests_7d ?? 0).toLocaleString()} in 7 days`} />
                <StatTile label="Error rate · 24h" icon={AlertTriangle} value={errorRate} hint={`${stats?.errors_24h ?? 0} failed requests`} />
                {attention ? (
                    <StatTile label="Needs attention" icon={Clock} tone="warn" value={<span className="text-base">{attention.name}</span>}>
                        <div className="text-xs text-amber-700">
                            {REASONS[attention.reason] || attention.reason}
                            {/* The fix for every reason (extend the expiry, pick another owner)
                                lives on the key's page; rolling would not move the expiry. */}
                            {keys.some((k) => k.key_id === attention.key_id) && (
                                <>
                                    {' · '}
                                    <button type="button" className="underline" data-testid="attention-open" onClick={() => onOpen(keys.find((k) => k.key_id === attention.key_id))}>
                                        Review key
                                    </button>
                                </>
                            )}
                        </div>
                    </StatTile>
                ) : (
                    <StatTile
                        label="Contacts via API · 7d"
                        icon={Users}
                        value={(stats?.contacts_created_7d ?? 0).toLocaleString()}
                        hint="New contacts your keys created this week. Expiring keys and orphaned owners show here instead."
                    />
                )}
            </div>

            <div className="flex flex-wrap items-center gap-3 px-4 py-3 border border-stone-200 rounded-xl bg-stone-50">
                <span className="text-xs font-semibold text-stone-600">Base URL</span>
                <code className="font-mono text-[12.5px] bg-white border border-stone-200 rounded-md px-2 py-1" data-testid="base-url">{base}</code>
                <button type="button" onClick={copyBase} className="h-7 px-2.5 rounded-md border border-stone-200 bg-white text-[12.5px] font-medium inline-flex items-center gap-1.5 hover:bg-stone-50">
                    <Copy className="w-3 h-3" /> Copy
                </button>
                <span className="flex-1" />
                <span className="text-xs text-stone-500">Send keys in the <span className="font-mono">Authorization: Bearer</span> header.</span>
            </div>

            {keys.length === 0 ? (
                <div className="border border-dashed border-stone-200 rounded-xl bg-stone-50 p-9 text-center text-sm text-stone-500" data-testid="keys-empty">
                    No keys yet. Create one to let another app add contacts and companies.
                </div>
            ) : (
                <div className="border border-stone-200 rounded-xl overflow-x-auto">
                    <table className="w-full min-w-[820px]">
                        <thead>
                            <tr className="text-left text-xs font-medium tracking-wider uppercase text-stone-500 border-b border-stone-200">
                                <th className="px-4 py-3">Name</th>
                                <th className="px-4 py-3">Permissions</th>
                                <th className="px-4 py-3">Default owner</th>
                                <th className="px-4 py-3">Last used</th>
                                <th className="px-4 py-3 text-right">24h</th>
                                <th className="px-4 py-3">Status</th>
                                <th className="px-4 py-3 w-12" />
                            </tr>
                        </thead>
                        <tbody>
                            {keys.map((k) => {
                                const revoked = k.status === 'revoked';
                                return (
                                    <tr
                                        key={k.key_id}
                                        data-testid={`key-row-${k.key_id}`}
                                        onClick={() => onOpen(k)}
                                        className={`border-b border-stone-100 last:border-b-0 hover:bg-stone-50 cursor-pointer ${revoked ? 'text-stone-400' : ''}`}
                                    >
                                        <td className="px-4 py-3 min-w-[230px]">
                                            <span className={`text-sm font-medium ${revoked ? 'text-stone-400' : 'text-stone-900'}`}>{k.name}</span>
                                            <span className="block text-xs text-stone-400 mt-0.5">
                                                <span className="font-mono text-[11.5px]">{k.display}</span>
                                                {' · '}
                                                {revoked ? `revoked by ${k.revoked_by?.name || 'an admin'}` : `by ${k.created_by?.name || '—'} · ${formatDate(k.created_at)}`}
                                            </span>
                                        </td>
                                        <td className={`px-4 py-3 max-w-[260px] ${revoked ? 'opacity-60' : ''}`}><ScopeChips scopes={k.scopes} /></td>
                                        <td className="px-4 py-3 text-sm whitespace-nowrap">
                                            {revoked ? '—' : (k.default_owner?.name || '—')}
                                            {!revoked && k.default_owner && !k.default_owner.active && (
                                                <span className="block text-xs text-amber-700">no longer active</span>
                                            )}
                                        </td>
                                        <td className="px-4 py-3 text-[13px] whitespace-nowrap">
                                            {relativeTime(k.last_used_at)}
                                            <span className="block font-mono text-[11px] text-stone-400">
                                                {k.ip_allowlist?.length ? 'IP locked' : (k.last_used_ip || '')}
                                            </span>
                                        </td>
                                        <td className="px-4 py-3 text-right tabular-nums text-sm">{(k.requests_24h || 0).toLocaleString()}</td>
                                        <td className="px-4 py-3"><KeyStatusPill apiKey={k} /></td>
                                        <td className="px-4 py-3 text-right" onClick={(e) => e.stopPropagation()}>
                                            {!revoked && (
                                                <DropdownMenu>
                                                    <DropdownMenuTrigger asChild>
                                                        <button type="button" aria-label={`Actions for ${k.name}`} className="w-8 h-8 rounded-md inline-flex items-center justify-center text-stone-500 hover:bg-stone-100">
                                                            <MoreHorizontal className="w-4 h-4" />
                                                        </button>
                                                    </DropdownMenuTrigger>
                                                    <DropdownMenuContent align="end">
                                                        <DropdownMenuItem onClick={() => onOpen(k)}><Eye className="w-4 h-4" /> View details</DropdownMenuItem>
                                                        {onEditPermissions && (
                                                            <DropdownMenuItem onClick={() => onEditPermissions(k)}><Pencil className="w-4 h-4" /> Edit permissions</DropdownMenuItem>
                                                        )}
                                                        {k.status !== 'expired' && (
                                                            <DropdownMenuItem onClick={() => onRoll(k)}><RefreshCw className="w-4 h-4" /> Roll key</DropdownMenuItem>
                                                        )}
                                                        <DropdownMenuSeparator />
                                                        <DropdownMenuItem className="text-red-600" onClick={() => onRevoke(k)}><Trash2 className="w-4 h-4" /> Revoke key</DropdownMenuItem>
                                                    </DropdownMenuContent>
                                                </DropdownMenu>
                                            )}
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}

            <Note icon={Lock} title="Keys belong to the organization, not to a person">
                A key keeps working when the admin who made it leaves, and it can only do what its permissions list. Every record it writes shows the key’s name in the activity history.
            </Note>
            <Note icon={Share2} title="Any key can credit a teammate">
                When a lead includes <span className="font-mono">attribution.referred_by</span> with someone’s Referral ID or email, that person gets the credit and owns the lead. Leads that name no one go to the key’s default owner. Adding a new app never needs a code change: create a key.
            </Note>
        </div>
    );
}
